import React, { useCallback, useMemo, useState } from 'react';
import PageArticle from '../../components/_template/page/PageArticle';
import PageTitle from '../../components/_molecules/page/PageTitle';
import { useParams } from 'react-router-dom';
import { logic } from '../../../logic';
import useLoading from '../../../hooks/useLoading';
import PageLoading from '../../components/_atom/loading/PageLoading';
import EmptyDataTemplate from '../dashboard/components/EmptyDataTemplate';
import BasicTable from '../../components/_atom/table/BasicTable';
import { PagingBar } from '../../components/_atom/paging/PagingBar';
import BasicButton from '../../components/_atom/button/BasicButton';
import { ButtonSize, ButtonTheme } from '../../components/_atom/button/StyledButton';
import refreshIcon from '../../assets/images/icon_refresh_grey.png';
import toast from 'react-hot-toast';
import { formatDateTimeOfUTC } from '../../../utils/TimeUtils';
import retryIcon from '../../assets/images/icon_retry_grey.png';
import { Webhook, WebhookEvent } from '../../assets/constant/webhookManager.type';
import IconDeleteRed from '../../assets/images/atom/icon_delete_red.png';
import DivideLine from '../../components/_atom/line/DivideLine';
import Switch from '../../components/_atom/switch/Switch';
import EditWebhookModal from '../tokenization/components/manager/webhook/EditWebhookModal';
import TagBadge, { TagBadgeType } from '../tokenization/components/TagBadge';
import DeleteWebhookModal from '../tokenization/components/manager/webhook/DeleteWebhookModal';
import KitContainer from '../tokenization/components/KitContainer';
import LabelValue from '../tokenization/components/LabelValue';

const WEBHOOK_HISTORY_COLGROUP = [3.3, 3.3, 3.3, 1.5, 3.5, 1.5];
const WEBHOOK_HISTORY_HEADERS = [
  { title: 'Webhook Name', accessor: 'webhookName' },
  { title: 'Event', accessor: 'event' },
  { title: 'Date(UTC)', accessor: 'date' },
  { title: 'Delivered', accessor: 'status' },
  { title: 'Response', accessor: 'result' },
  { title: 'Retry', accessor: 'retry' },
];
const WEBHOOK_HISTORY_LIMIT = 10;
const WebhookDetail = () => {
  const params = useParams();
  const { webhookId } = params;
  const [webhookDetail, setWebhookDetail] = useState<Webhook>();
  const getWebhookDetail = async (webhookId: number) => {
    const { result, error } = await logic.webhook.getWebhookDetail(webhookId);
    if (error) {
      return;
    }
    setWebhookDetail(result);
  };
  const changeWebhookActive = async (isActive: boolean, webhookId?: number) => {
    if (!webhookId) {
      return;
    }
    const { result, error } = await logic.webhook.updateSubscribeWebhook(webhookId, { isActive });
    if (error) {
      toast.error('Failed to update webhook');
      return;
    }
    setWebhookDetail(result);
  };
  const datas = useMemo(() => {
    const type = webhookDetail?.is_active === 1 ? TagBadgeType.BLUE : TagBadgeType.GREY;
    const text = webhookDetail?.is_active === 1 ? 'Active' : 'Deactivate';
    return [
      { label: 'Name', value: webhookDetail?.name },
      { label: 'ID', value: webhookDetail?.subscription_id },
      { label: 'URL', value: webhookDetail?.webhook_url },
      {
        label: 'Status',
        value: (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Switch
              isOn={webhookDetail?.is_active === 1}
              onToggle={() => changeWebhookActive(webhookDetail?.is_active === 0, webhookDetail?.subscription_id)}
            />
            <TagBadge type={type} text={text} />
          </div>
        ),
      },
      { label: 'Service', value: webhookDetail?.resource_subscriptions[0].service_identifier },
      { label: 'Event Type', value: webhookDetail?.event_name },
    ];
  }, [webhookDetail]);
  const { loading } = useLoading({
    synchronousFunction: async () => await getWebhookDetail(Number(webhookId)),
    dependency: [webhookId],
  });

  const [testResult, setTestResult] = useState<any>();
  const testWebhook = async () => {
    if (!webhookDetail) {
      return;
    }
    const { result, error } = await logic.webhook.requestTestWebhook(webhookDetail?.subscription_id);
    if (error) {
      toast.error('Failed to request webhook');
      return;
    }
    setTestResult(result?.response_details);
    toast.success('Webhook request success');
  };

  const [eventHistories, setEventHistories] = useState<any[]>([]);
  const [maxEventHistoriesNum, setMaxEventHistoriesNum] = useState(0);
  const getEventHistories = async (option?: { lastId?: number; offset?: number; order?: 'ASC' | 'DESC' }) => {
    const { result, error } = await logic.webhook.getWebhookEventHistoryList(Number(webhookId), { limit: WEBHOOK_HISTORY_LIMIT, ...option });
    if (error) {
      return;
    }
    const { data: eventHistories, total: maxEventHistoriesNum } = result;
    setEventHistories(eventHistories);
    if (!option?.offset) {
      setMaxEventHistoriesNum(maxEventHistoriesNum);
    }
  };
  const eventHistoryPageNum = Number(new URLSearchParams(window.location.search).get('event')) || 1;
  const maxEventHistoriesPageNum =
    maxEventHistoriesNum % WEBHOOK_HISTORY_LIMIT === 0
      ? maxEventHistoriesNum / WEBHOOK_HISTORY_LIMIT
      : Math.floor(maxEventHistoriesNum / WEBHOOK_HISTORY_LIMIT) + 1;
  const [isRefresh, setIsRefresh] = useState(false);
  const { loading: getEventHistoriesLoading } = useLoading({
    dependency: [eventHistoryPageNum, isRefresh],
    synchronousFunction: async () => {
      await getEventHistories({ offset: (eventHistoryPageNum - 1) * WEBHOOK_HISTORY_LIMIT });
    },
  });
  const [isEditWebhookModalOpen, setIsEditWebhookModalOpen] = useState(false);
  const [isDeleteWebhookModalOpen, setIsDeleteWebhookModalOpen] = useState(false);
  const retryWebhookEvent = async (webhook: any) => {
    const { error } = await logic.webhook.retryFailedSubscribeWebhook(webhook.subscription_id, webhook.webhook_id);
    if (error) {
      toast.error('Failed to retry webhook');
      return;
    }
    setIsRefresh(!isRefresh);
    toast.success('Retry Success!');
  };
  const WEBHOOK_HISTORY_RENDERER = useCallback((data: WebhookEvent) => {
    const tagBadgeType = () => {
      if (data.status === 'SUCCEED') {
        return TagBadgeType.BLUE;
      } else if (data.status === 'FAILED') {
        return TagBadgeType.RED;
      } else if (data.status === 'CREATED') {
        return TagBadgeType.GREEN;
      }
      return TagBadgeType.GREY;
    };
    return {
      date: formatDateTimeOfUTC(data.created_at),
      webhookName: data.name ? data.name : 'Deleted Webhook',
      event: data.service_identifier,
      status: <TagBadge type={tagBadgeType()} text={data.status} />,
      result: data.webhook_logs[-1]?.http_status_code,
      retry: (
        <>
          {data.name && data.status === 'FAILED' && (
            <BasicButton
              size={ButtonSize.DYNAMIC}
              theme={ButtonTheme.NORMAL}
              onClick={() => retryWebhookEvent(data)}
              style={{ width: 'fit-content', height: 'fit-content', padding: '5px 8px' }}
            >
              <img src={retryIcon} alt="retry" style={{ width: 14, height: 14, objectFit: 'contain', marginRight: 10 }} />
              Retry
            </BasicButton>
          )}
        </>
      ),
    };
  }, []);

  return (
    <PageArticle>
      <EditWebhookModal
        isOpen={isEditWebhookModalOpen}
        closeModal={() => setIsEditWebhookModalOpen(false)}
        webhook={webhookDetail}
        setWebhook={setWebhookDetail}
      />
      <DeleteWebhookModal isOpen={isDeleteWebhookModalOpen} closeModal={() => setIsDeleteWebhookModalOpen(false)} webhook={webhookDetail} />
      <PageTitle title={'Webhook Detail'} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: 40 }}>
        <KitContainer title={'Webhook Basic Info'}>
          {datas.map((data, index) => {
            return <LabelValue key={index} label={data.label} value={data.value} />;
          })}
        </KitContainer>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <BasicButton theme={ButtonTheme.NORMAL} onClick={() => setIsEditWebhookModalOpen(true)}>
            Edit
          </BasicButton>
          <BasicButton
            size={ButtonSize.ICON}
            theme={ButtonTheme.MINOR}
            onClick={() => setIsDeleteWebhookModalOpen(true)}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 40,
              height: 40,
              padding: 0,
            }}
          >
            <img src={IconDeleteRed} style={{ width: 18, objectFit: 'contain' }} alt={'delete'} />
          </BasicButton>
        </div>
        <KitContainer title={'Webhook Test'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div
              style={{
                display: 'flex',
                padding: '8px 14px',
                borderRadius: 12,
                background: '#F9F9F9',
                fontWeight: 400,
                fontSize: 16,
                lineHeight: '30px',
              }}
            >
              <div style={{ color: '#7D828D', fontWeight: 500, whiteSpace: 'pre' }}>{'URL : '}</div>
              {webhookDetail?.webhook_url}
            </div>
            <BasicButton size={ButtonSize.DYNAMIC} theme={ButtonTheme.PRIMARY} onClick={() => testWebhook()} style={{ width: 'fit-content' }}>
              Try Test
            </BasicButton>
          </div>
          {testResult && (
            <>
              <DivideLine />
              <div style={{ padding: '8px 14px', borderRadius: 12, background: '#EFEFEF' }}>
                <div style={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', marginBottom: 10 }}>Test Result</div>
                <pre>
                  <code>{JSON.stringify(testResult, null, 2)}</code>
                </pre>
              </div>
            </>
          )}
        </KitContainer>
        <KitContainer title={'Event History'}>
          {getEventHistoriesLoading ? (
            <PageLoading style={{ height: 477 }} />
          ) : (
            <>
              {eventHistories.length === 0 ? (
                <EmptyDataTemplate>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 500,
                      lineHeight: '21px',
                      color: '#7D828D',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    No event history yet
                  </div>
                </EmptyDataTemplate>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                  }}
                >
                  <BasicTable data={eventHistories} colGroup={WEBHOOK_HISTORY_COLGROUP} headers={WEBHOOK_HISTORY_HEADERS} renderer={WEBHOOK_HISTORY_RENDERER} />
                  <PagingBar maxPageNum={maxEventHistoriesPageNum} query={'event'} />
                </div>
              )}
            </>
          )}
          <div
            style={{
              display: 'flex',
              position: 'absolute',
              top: 22,
              right: 30,
            }}
          >
            <BasicButton size={ButtonSize.DYNAMIC} theme={ButtonTheme.NORMAL} onClick={() => setIsRefresh(!isRefresh)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <img src={refreshIcon} alt={'⟳'} width={14} /> Refresh
              </div>
            </BasicButton>
          </div>
        </KitContainer>
      </div>
    </PageArticle>
  );
};

export default WebhookDetail;
